import type Ajv from "ajv";
import type { ValidateFunction } from "ajv";

import { pointerSegment } from "./schema";
import type {
	JsonSchemaDefinition,
	ValidationError
} from "./types";
import { isRecord } from "./values";

export interface FileInputState {
	status: "reading" | "failed" | "selected";
	size?: number;
}

/** Validate file attempts that cannot yet be represented by a serialized schema value. */
export class FileInputValidation {
	private errors = new Map<string, ValidationError[]>();

	private readonly validators = new Map<string, ValidateFunction>();

	public constructor(private readonly ajv: Ajv) {}

	public set(path: string, input: FileInputState | undefined, schema: JsonSchemaDefinition): void {
		if(!input) {
			this.errors.delete(path);
			return;
		}

		const maximum = schema.metadata?.maxSize;
		const oversized = maximum !== undefined && input.size !== undefined && input.size > maximum;
		const customMessage: unknown = schema.errorMessage;
		const sizeMessage = typeof customMessage === "string" ? customMessage
			: isRecord(customMessage) && typeof customMessage.metadata === "string" ? customMessage.metadata
				: "must not exceed the configured file size limit";
		const validationSchema = oversized
			? {
				type: "number",
				maximum,
				errorMessage: sizeMessage
			}
			: {
				const: "selected",
				errorMessage: input.status === "reading"
					? "Please wait for the file to finish loading."
					: "The file could not be read. Please choose it again."
			};
		const key = JSON.stringify(validationSchema);
		let validate = this.validators.get(key);

		if(!validate) {
			validate = this.ajv.compile(validationSchema);
			this.validators.set(key, validate);
		}

		validate(oversized ? input.size : input.status);

		const instancePath = path ? "/" + path.split(".").map(pointerSegment)
			.join("/") : "";
		const atField = (error: ValidationError): ValidationError => ({
			...error,
			instancePath,
			params: {
				...error.params,
				...(Array.isArray(error.params.errors)
					? { errors: (error.params.errors as ValidationError[]).map(atField) }
					: {})
			}
		});
		this.errors.set(path, (validate.errors ?? []).map(atField));
	}

	public get currentErrors(): ValidationError[] {
		return [...this.errors.values()].flat();
	}

	public clearHidden(isVisible: (path: string) => boolean): void {
		for(const path of this.errors.keys()) {
			if(!isVisible(path)) {
				this.errors.delete(path);
			}
		}
	}

	public pathOf(error: ValidationError): string | undefined {
		return [...this.errors].find(([, errors]) => errors.includes(error))?.[0];
	}

	public clearSubtree(path: string): void {
		for(const candidate of this.errors.keys()) {
			if(path === "" || candidate === path || candidate.startsWith(path + ".")) {
				this.errors.delete(candidate);
			}
		}
	}

	public removeRow(path: string, index: number): void {
		const prefix = path ? `${path}.` : "";
		const shifted = new Map<string, ValidationError[]>();

		for(const [candidate, errors] of this.errors) {
			const [segment, ...remaining] = candidate.slice(prefix.length).split(".");
			const row = Number(segment);

			if(!candidate.startsWith(prefix) || !/^\d+$/.test(segment) || row < index) {
				shifted.set(candidate, errors);
			} else if(row > index) {
				const nextPath = `${prefix}${[row - 1, ...remaining].join(".")}`;
				const instancePath = "/" + nextPath.split(".").map(pointerSegment)
					.join("/");
				const move = (error: ValidationError): void => {
					// Keep identity so an asynchronous reader can still recognize its attempt.
					error.instancePath = instancePath;
					if(Array.isArray(error.params.errors)) {
						(error.params.errors as ValidationError[]).forEach(move);
					}
				};

				errors.forEach(move);
				shifted.set(nextPath, errors);
			}
		}

		this.errors = shifted;
	}
}
